import React, { useContext, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { dbPost, dbForm } from '../../apis/commonApi';
import TextEditor from '../../components/inputComponents/TextEditor';
import FileUpload from '../../components/modalComponents/FileUpload';
import TextViewer from '../../components/inputComponents/TextViewer';
import GbitTablePaging from '../../components/tableComponents/GbitTablePaging';
import { GlobalContext } from '../../components/contexts';
import { nlPost } from '../../apis/notLoginApi';

const AnsQuestion = () => {
  useEffect(() => {
    document.title = '문의 답변';
  }, []);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [answers, setAnswers] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('ALL');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [questionDetails, setQuestionDetails] = useState(null);
  const [isDetailView, setIsDetailView] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const editorRef = useRef(null);
  const [isFileModalOpen, setIsFileModalOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [sharedFileId, setSharedFileId] = useState(null);
  const [currentFileSeq, setCurrentFileSeq] = useState(null);
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const { store } = useContext(GlobalContext);

  const handleBackToList = () => {
    setSelectedAnswer(null);
    setIsDetailView(false);
    resetFileState();
    handleSearch();
  };

  const handleFileUpload = uploadedFilePath => {
    const cleanPath = uploadedFilePath.replace(/^.*[\\/]/, '');
    const uploadedUrl = `${process.env.REACT_APP_BACKEND_URL}/files/${cleanPath}`;
    if (editorRef.current) {
      const editorInstance = editorRef.current.getInstance();
      const currentContent = editorInstance.getMarkdown();
      const imageMarkdown = `![이미지 설명](${uploadedUrl})`;
      editorInstance.setMarkdown(`${currentContent}\n${imageMarkdown}`);
    }
  };

  const resetFileState = () => {
    setSelectedFiles([]);
    setUploadedFiles([]);
    setSharedFileId(null);
    setCurrentFileSeq(null);
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const url = '/cs/category/list.do';
        const response = await nlPost(url, {});
        if (response.length > 0) {
          const filteredCategories = response.filter(cat => cat.useYn === 'Y');
          setCategories(filteredCategories);
        }
      } catch (error) {}
    };

    const initialize = async () => {
      await fetchCategories();
      handleSearch();
    };

    initialize();
  }, []);

  const handleSubmit = async () => {
    const content = editorRef.current.getInstance().getMarkdown();
    if (!title.trim()) {
      alert('제목을 입력해주세요.');
      return;
    }
    if (title.length > 33) {
      alert('제목은 33글자를 초과할 수 없습니다.');
      return;
    }
    if (!content.trim()) {
      alert('내용을 입력해주세요.');
      return;
    }
    if (content.length > 1000) {
      alert('내용은 1000글자를 초과할 수 없습니다.');
      return;
    }

    const apiUrl = '/cs/ask/answer/update.do';
    const payload = {
      ansTtl: title,
      ansCn: content,
      fileId: sharedFileId,
      ansId: selectedAnswer.ansId,
    };

    try {
      await dbPost(apiUrl, payload);
      alert(`답변이 성공적으로 수정 되었습니다.`);

      setIsDetailView(false);
      resetFileState();
      setTitle('');
      setContent('');
      handleSearch();
    } catch (error) {
      alert(`답변 수정 중 문제가 발생했습니다.`);
    }
  };

  const fetchAnswers = async (filters = {}) => {
    try {
      const url = '/cs/ask/answer/search.do';
      const response = await dbPost(url, filters);
      setAnswers(Array.isArray(response) ? response : []);
    } catch (error) {
      setAnswers([]);
    }
  };

  const handlePageChange = page => {
    setCurrentPage(page);
  };

  const paginatedData = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return answers.slice(startIndex, startIndex + itemsPerPage);
  };

  const handleSearch = (period = selectedPeriod) => {
    const filters = {
      ctgryId: selectedCategory || undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
    };

    if (period && period !== 'ALL') {
      const parsedPeriod = parseInt(period);
      if (isNaN(parsedPeriod)) {
        return;
      }

      const today = new Date();
      const startDate = new Date(today);
      startDate.setMonth(startDate.getMonth() - parsedPeriod);

      filters.startDate = startDate
        .toISOString()
        .split('T')[0]
        .replace(/-/g, '.');
      filters.endDate = today.toISOString().split('T')[0].replace(/-/g, '.');
    }

    fetchAnswers(filters);
  };

  const handleDetailClick = async answer => {
    setSelectedAnswer(answer);
    setIsDetailView(true);
    setTitle(answer.ansTtl);
    setContent(answer.ansCn);

    try {
      const url = '/cs/ask/view.do';
      const response = await dbPost(url, { qstnId: answer.qstnId });
      setQuestionDetails(response || null);
    } catch (error) {}
  };

  const renderList = () => (
    <div className="contents">
      <div className="page-title-wrap" data-type="responsive">
        <h2 className="h-tit">답변 목록</h2>
      </div>
      <ul className="guide-txt">
        <li>답변 목록을 조회하고 관련 문의 내용을 확인할 수 있습니다.</li>
      </ul>
      <div className="tab full fill tab-mutation">
        <ul role="tablist">
          {['1', '3', '6', 'ALL'].map((period, index) => (
            <li
              key={index}
              id={`tab_0${index}`}
              role="tab"
              aria-selected={selectedPeriod === period}
              className={selectedPeriod === period ? 'active' : ''}
            >
              <button
                type="button"
                className="btn-tab"
                onClick={() => {
                  setSelectedPeriod(period);
                  handleSearch(period);
                }}
              >
                {period === 'ALL' ? '전체' : `${period}개월`}
              </button>
            </li>
          ))}
        </ul>
      </div>
      <div className="search-top-box">
        <div className="sch-filter-box flex">
          <div className="filter-form">
            <label className="label" htmlFor="appl-sch-sel1">
              카테고리
            </label>
            <select
              id="appl-sch-sel1"
              className="form-select md"
              value={selectedCategory}
              onChange={e => setSelectedCategory(e.target.value)}
            >
              <option value="">전체</option>
              {categories
                .filter(cat => cat.ctgryId !== 'NOTICE')
                .map(cat => (
                  <option key={cat.ctgryId} value={cat.ctgryId}>
                    {cat.ctgryNm}
                  </option>
                ))}
            </select>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <label className="hidden-label" htmlFor="startDate">
                조회 기간 시작
              </label>
              <input
                type="date"
                aria-labelledby="startDate"
                className="form-control"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                id="startDate"
              />
              ~
              <label className="hidden-label" htmlFor="endDate">
                조회 기간 끝
              </label>
              <input
                type="date"
                className="form-control"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                aria-labelledby="endDate"
                id="endDate"
              />
            </div>
            <button
              type="button"
              className="btn md primary"
              onClick={() => handleSearch()}
            >
              검색
            </button>
          </div>
        </div>
      </div>
      <div className="tbl-wrap">
        <table className="tbl col data scroll">
          <caption>문의사항 답변 목록입니다.</caption>
          <thead>
            <tr>
              <th>카테고리</th>
              <th>답변 제목</th>
              <th>등록일</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData().map(answer => (
              <tr
                key={answer.ansId}
                onClick={() => handleDetailClick(answer)}
                className="clickable-row"
              >
                <td>
                  <span className="krds-badge outline">
                    {categories.find(cat => cat.ctgryId === answer.ctgryId)
                      ?.ctgryNm || '미정'}
                  </span>
                </td>
                <td>
                  <strong>{answer.ansTtl}</strong>
                </td>
                <td>{answer.regDt}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <GbitTablePaging
        totalItems={answers.length}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage}
        onPageChange={handlePageChange}
      />
    </div>
  );

  const renderDetailView = () => (
    <div className="contents">
      <div className="page-title-wrap" data-type="responsive">
        <h2 className="h-tit">답변 상세</h2>
      </div>
      <div className="tbl-wrap">
        <table className="tbl data row">
          <caption>문의 답변 내역 등록 테이블입니다.</caption>
          <tbody>
            <tr>
              <th>카테고리</th>
              <td>
                <div className="input-group">
                  {categories.find(
                    cat => cat.ctgryId === selectedAnswer?.ctgryId
                  )?.ctgryNm || '미정'}
                </div>
              </td>
            </tr>
            <tr>
              <th>문의 제목</th>
              <td>{questionDetails?.qstnTtl || '조회 중...'}</td>
            </tr>
            <tr>
              <th>문의 내용</th>
              <td>
                <TextViewer content={questionDetails?.qstnCn || '조회 중...'} />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="contents">
        <div className="tbl-wrap">
          <table className="tbl data row">
            <caption>문의 답변 입력 테이블입니다.</caption>
            <tbody>
              <tr>
                <th>답변 제목</th>
                <td>
                  <div className="input-group">
                    <label className="hidden-label" htmlFor="answerTitle">
                      라벨
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="answerTitle"
                      placeholder="제목을 입력해주세요."
                      value={title}
                      onChange={e => setTitle(e.target.value)}
                      aria-labelledby="answerTitle"
                    />
                  </div>
                </td>
              </tr>
              <tr>
                <th>답변 내용</th>
                <td>
                  <TextEditor
                    className="form-control"
                    ref={editorRef}
                    initialValue={selectedAnswer.ansCn}
                  />
                </td>
              </tr>
              <tr>
                <th>첨부파일</th>
                <td>
                  <div className="input-group">
                    <button
                      type="button"
                      className="btn tertiary"
                      onClick={() => {
                        setIsFileModalOpen(true);
                      }}
                    >
                      파일 첨부
                    </button>
                  </div>
                  {uploadedFiles.length > 0 && (
                    <p>첨부된 파일 개수: {uploadedFiles.length}개</p>
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div className="page-btn-wrap both">
        <button
          type="button"
          className="btn xlg tertiary"
          onClick={handleBackToList}
        >
          목록으로
        </button>
        <button
          type="button"
          className="btn xlg primary"
          onClick={handleSubmit}
        >
          답변 수정
        </button>
      </div>
      {isFileModalOpen && (
        <FileUpload
          onClose={() => setIsFileModalOpen(false)}
          dbPost={dbPost}
          dbForm={dbForm}
          selectedFiles={selectedFiles}
          setSelectedFiles={setSelectedFiles}
          uploadedFiles={uploadedFiles}
          setUploadedFiles={setUploadedFiles}
          sharedFileId={sharedFileId}
          setSharedFileId={setSharedFileId}
          currentFileSeq={currentFileSeq}
          setCurrentFileSeq={setCurrentFileSeq}
          onUpload={handleFileUpload}
        />
      )}
    </div>
  );

  return (
      <div className="inner in-between">
        {isDetailView ? renderDetailView() : renderList()}
      </div>
  );
};

export default AnsQuestion;
